import MoreVertIcon from "@mui/icons-material/MoreVert";
import NotInterestedIcon from "@mui/icons-material/NotInterested";
import {
    Alert,
    Button,
    IconButton,
    Menu,
    MenuItem,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import { withStyles } from "@mui/styles";
import axios from "axios";
import * as React from "react";

import { LoadingWrapper } from "../../components/LoadingWrapper";
import { Helpers } from "../../utils/helpers";
import { PopupMessage } from "../../utils/message";

import {Config} from "../../config/config";
import { EditChest } from "./edit_chest";
import { NewChest } from "./new_chest";
import { SimulateChest } from "./simulate";

const sheetsUrl = "https://docs.google.com/spreadsheets/d/17G6JEFGMs7fTJPZXv9PLcgtYa_nQe6DOtEYpbzjxNOs";

interface ChestsStates {
    chests: ChestData[];
    loaded: boolean;
    anchorEl: any;
    menuVisible: boolean;
    activeSimulationItem: ChestData | null;
    simulateDialogVisible: boolean;
}

interface ChestsProps {
    activeUser: any;
    classes: any;

}

export interface ChestData {
    drive_key: string;
    name: string;
    graphic_id: string;
    description: string;
    currency: string;
    tracked_value: string;
    price: number;
    chest_id: string;
    created_at: string;
    updated_at: string;
    localization_title: string;
    localization_desc: string;
    rarity: string;
}

class ChestsWrapped extends React.Component<ChestsProps, ChestsStates> {

    constructor(props) {
        super(props);
        // List with grid items
        this.state = {
            chests: [],
            loaded: false,
            anchorEl: null,
            menuVisible: false,
            // Simulation
            activeSimulationItem: null,
            simulateDialogVisible: false,
        };

        document.title = Helpers.documentTitle("Briefcases");
    }

    componentDidMount() {
        this.fetchChests();
    }

    refreshList = () => this.setState({loaded: false}, () => {
        this.fetchChests();
    });

    openRowMenu = (event) => this.setState({anchorEl: event.currentTarget}, () => this.setState({menuVisible: true}));
    closeRowMenu = () => this.setState({menuVisible: false});
    clearMenuAnchor = () => this.setState({anchorEl: null});
    closeSimulateDialog = () => this.setState({simulateDialogVisible: false});

    fetchChests = async () => {
        try {
            // Put data to S3
            const response = await axios.get(`${Config.getInstance.apiUrl}/api/chests`, {
                headers: {
                    Authorization: Helpers.getSessionId(),
                },
            });

            this.setState({chests: response.data.data, loaded: true});
        } catch (error) {
            this.setState({loaded: true});
            PopupMessage.error("Something went wrong");
        }
    };

    getFileActions = () => {
        if (this.state.anchorEl) {
            const chestId = this.state.anchorEl.getAttribute("chest-key");
            const chestObject = this.state.chests.find(chest => chest.chest_id === chestId);

            if (chestObject) {
                return [
                    <SimulateChest
                        chest={chestObject}
                        closeRowMenu={this.closeRowMenu}
                        key="simulate-chest-key"
                    />,
                    <EditChest
                        chest={chestObject}
                        refreshParent={this.refreshList}
                        closeRowMenu={this.closeRowMenu}
                        key="edit-chest-key"
                    />,
                    <MenuItem onClick={() => this.viewSheets(null, chestObject.drive_key)} key="sheets-chest-key">
                        Google sheet
                    </MenuItem>,
                ];
            }
        }

        return;
    };

    openDropLevelsSheet = () => {
        const win = window.open(sheetsUrl, "_blank");
        if (win) {
            win.focus();
        }
    };

    openSimulation = (event, chestObject) => {
        if (!event || event._dispatchListeners.length === 1) {
            this.closeRowMenu();
            this.setState({activeSimulationItem: chestObject, simulateDialogVisible: true});
        }
    };

    viewSheets = (event, driveKey) => {
        if (!event || event._dispatchListeners.length === 1) {
            const win = window.open(`https://docs.google.com/spreadsheets/d/${driveKey}/edit`, "_blank");
            if (win) {
                win.focus();
            }
        }
    };

    formatChestsToTable = (chests) => {
        if (!this.state.loaded) {
            return <TableRow key="loading-row">
                <TableCell colSpan={7}>
                    {Helpers.renderSkeletonRows(5, 50)}
                </TableCell>
            </TableRow>
        }

        if (chests.length === 0) {
            return <TableRow key="records_not_found">
                <TableCell colSpan={7}>
                    <Alert severity="info">No chests</Alert>
                </TableCell>
            </TableRow>
        }

        const tracked_values = {
            hq: "HQ Level",
            league: "League",
            rank: "Rank",
            vipLevel: "Vip Level",
            gemRollEvent: "Gem roll event"
        };

        return chests.map(chest => (
            <TableRow key={chest.chest_id} hover={true}>
                <TableCell>{chest.chest_id}</TableCell>
                <TableCell>{chest.name}</TableCell>
                <TableCell>{chest.description}</TableCell>
                <TableCell>{chest.currency !== 99 && chest.price} {Helpers.chestPriceLabel(chest.currency)}</TableCell>
                <TableCell>{chest.tracked_value ? tracked_values[chest.tracked_value] : "HQ Level"}</TableCell>
                <TableCell>{Helpers.formatDateWithTime(chest.updated_at)}</TableCell>
                <TableCell style={{textAlign: "right"}}>
                    <IconButton
                        aria-label="More"
                        aria-haspopup="true"
                        onClick={this.openRowMenu}
                        chest-key={chest.chest_id}
                    >
                        <MoreVertIcon/>
                    </IconButton>
                </TableCell>
            </TableRow>
        ));
    };

    formatTableBody(chests) {
        return <React.Fragment>
            <Table className="min-table-size">
                <TableHead>
                    <TableRow>
                        <TableCell style={{width: 20}}>Id</TableCell>
                        <TableCell style={{width: 300}}>Name</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell style={{width: 150}}>Price</TableCell>
                        <TableCell style={{width: 150}}>Tracked Value</TableCell>
                        <TableCell style={{width: 200}}>Last modified</TableCell>
                        <TableCell style={{width: 100}}></TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {this.formatChestsToTable(chests)}
                </TableBody>
            </Table>
            <Menu
                id="long-menu"
                anchorEl={this.state.anchorEl}
                open={this.state.menuVisible}
                onClose={this.closeRowMenu}
                TransitionProps={{
                    onExited: this.clearMenuAnchor
                }}
            >
                {this.getFileActions()}
            </Menu>
        </React.Fragment>;
    };

    render() {
        const {classes} = this.props;

        return <div className="contentPadding">
            <Paper className="root" elevation={0}>
                <div style={{float: "left", padding: "15px 22px 15px 22px", width: "100%"}}>
                    <div className={classes.title}>
                        <Typography variant="h6" id="tableTitle">
                            Briefcases
                        </Typography>
                    </div>
                    <div className={classes.actions}>
                        <div style={{marginLeft: 15, display: "inline-block"}}>
                            <Button disableElevation color="primary" onClick={this.openDropLevelsSheet} variant="contained" size="small">Drop levels</Button>
                        </div>
                        <div style={{marginLeft: 15, display: "inline-block"}}>
                            <NewChest
                                buttonText="New Briefcase"
                                refreshParent={this.refreshList}
                            />
                        </div>
                    </div>
                </div>

                <div style={{float: "left", width: "100%"}}>
                    {this.formatTableBody(this.state.chests)}
                </div>
            </Paper>
        </div>;
    }
}

const styles: any = theme => ({
    title: {
        flex: "0 0 auto",
        float: "left",
    },
    actions: {
        color: theme.palette.text.secondary,
        float: "right",
    },
});

export const Chests = withStyles(styles)(ChestsWrapped);
